/**
 * State a new state - with what makes it one - issue #50.
 *
 * It was the generic new-element dialog: a package, an ID, a name and a
 * free-text type. A state is more than that and none of it could be said
 * there: whether it is a MODE or a PHASE (both derived from what it
 * specializes, so only by typing the right base name), which parts exhibit it,
 * what it includes, whether its machine STARTS in it, and how it joins the
 * states around it.
 *
 * TRANSITIONS COME WITH IT, the way attributes come with a new part: rows in
 * the same request, all checked before anything is written, one commit
 * (Patrick, 2026-09-28). A row is one end - the new state is always the
 * other - so it is a direction and a state, and only the states a transition
 * with the new one could be declared for are offered.
 */

import { useEffect, useState } from 'react'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { ApiError, api } from '../../api'
import type { PackageRow, StateTransitionRow } from '../../schema'
import { useSchema } from '../../state/AppData'
import { MONOSPACE } from '../../theme'
import ElementPicker from '../common/ElementPicker'
import { useElements } from '../common/elements'
import type { PickerOption } from '../common/elements'
import {
  STATE_SORTS,
  canJoin,
  currentStart,
  offeredAsDecider,
  stateName,
  stateRefs,
} from './state-authoring'
import type { Direction } from './state-authoring'

/** One transition row in the form, keyed for React. */
interface Row extends StateTransitionRow {
  key: string
}

const emptyRow = (key: string): Row => ({
  key, direction: 'to', state: '', trigger: '', guard: '', effect: '', decided_by: '',
})

export default function CreateStateDialog({
  inside: fixed,
  onClose,
  onCreated,
}: {
  /** Where it goes, when that is already decided: a machine, or a package. */
  inside?: { qualifiedName: string; label: string }
  onClose: () => void
  onCreated: (identifier: string) => void
}) {
  const schema = useSchema()
  const states = useElements(schema, 'states')
  const parts = useElements(schema, 'parts')
  const [packages, setPackages] = useState<PackageRow[]>([])
  const [inside, setInside] = useState(fixed?.qualifiedName ?? '')
  const [identifier, setIdentifier] = useState('')
  const [name, setName] = useState('')
  const [sort, setSort] = useState<string>(STATE_SORTS[0].value)
  const [statement, setStatement] = useState('')
  const [exhibitedBy, setExhibitedBy] = useState<string[]>([])
  const [includes, setIncludes] = useState<string[]>([])
  const [initial, setInitial] = useState(false)
  const [rows, setRows] = useState<Row[]>([])
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')

  // Behavior and under (issue #114).
  const [packageWarning, setPackageWarning] = useState('')
  useEffect(() => {
    if (fixed) return
    api
      .packagesFor('state')
      .then((answer) => {
        setPackages(answer.items)
        setPackageWarning(answer.warning)
      })
      .catch(() => setPackages([]))
  }, [fixed])

  const refs = stateRefs(states.rows)
  // What goes INSIDE a state is only a state definition - OWNING_KEYWORDS maps
  // 'state' to ('state def',) - so a usage is not offered as somewhere to go.
  const machines = states.rows.filter((row) => row.is_definition !== false)
  const machine = states.rows.find((row) => row.qualified_name === inside)
  const label = (name || identifier).trim()
  const qualified = inside && label ? `${inside}::${label}` : ''
  const startsNow = machine ? currentStart(states.rows, machine.id) : undefined

  const insideOptions: PickerOption[] = [
    ...packages.map((entry) => ({ value: entry.qualified_name, label: entry.name, detail: 'package' })),
    ...machines.map((row) => ({
      value: String(row.qualified_name), label: stateName(row), detail: `state · ${row.qualified_name}`,
    })),
  ]

  const change = (key: string, patch: Partial<Row>) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)))

  const rowBroken = (row: Row) => !row.state
  const deciderIds = new Set(parts.rows.map((row) => row.id))
  const deciderBroken = (row: Row) =>
    parts.complete && Boolean(row.decided_by) && !deciderIds.has(String(row.decided_by))
  const blocked = !inside || !identifier.trim() || rows.some((row) => rowBroken(row) || deciderBroken(row))

  const submit = () => {
    setBusy(true)
    setFailure('')
    api
      .create('states', {
        package: inside,
        identifier: identifier.trim(),
        ...(name.trim() ? { name: name.trim() } : {}),
        type_name: sort,
        ...(statement.trim() ? { statement: statement.trim() } : {}),
        ...(exhibitedBy.length ? { exhibited_by: exhibitedBy } : {}),
        ...(includes.length ? { includes } : {}),
        initial: initial && Boolean(machine),
        transitions: rows.map(({ key: _key, ...row }) => row),
      })
      .then((created) => onCreated(created.identifier))
      .catch((error: ApiError) => setFailure(error.message))
      .finally(() => setBusy(false))
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{fixed ? `New state inside ${fixed.label}` : 'New state'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {failure && <Alert severity="error">{failure}</Alert>}

          {fixed ? (
            <TextField
              label="Inside"
              size="small"
              value={fixed.qualifiedName}
              slotProps={{ input: { readOnly: true, sx: { fontFamily: MONOSPACE } } }}
            />
          ) : (
            <ElementPicker
              options={insideOptions}
              value={inside || null}
              onChange={setInside}
              label="Inside"
              required
              monospace
              helperText={packageWarning || 'A package, or the state machine it is a state of'}
            />
          )}

          <Stack direction="row" spacing={2}>
            <TextField
              label="ID"
              required
              size="small"
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
              helperText="The short name, e.g. ST_SAFE"
              slotProps={{ htmlInput: { style: { fontFamily: MONOSPACE } } }}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Name"
              size="small"
              value={name}
              onChange={(event) => setName(event.target.value)}
              helperText="What transitions name it by; defaults to the ID"
              sx={{ flex: 1 }}
            />
            <TextField
              select
              label="Sort"
              size="small"
              value={sort}
              onChange={(event) => setSort(event.target.value)}
              helperText="A mode is commanded; a phase is a mission segment"
              sx={{ minWidth: 140 }}
            >
              {STATE_SORTS.map((choice) => (
                <MenuItem key={choice.value} value={choice.value}>
                  {choice.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <TextField
            label="Description"
            size="small"
            multiline
            minRows={2}
            value={statement}
            onChange={(event) => setStatement(event.target.value)}
          />

          <ElementPicker
            multiple
            rows={parts.rows}
            where={offeredAsDecider}
            value={exhibitedBy}
            onChange={setExhibitedBy}
            label="Exhibited by"
            helperText="The parts that are in this state"
          />
          <ElementPicker
            multiple
            rows={states.rows}
            describe={(row, value) => ({ value, label: stateName(row), detail: String(row.qualified_name ?? '') })}
            show="label"
            value={includes}
            onChange={setIncludes}
            label="Includes"
            helperText="States of other machines that hold while this one does"
          />

          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={initial}
                  disabled={!machine}
                  onChange={(event) => setInitial(event.target.checked)}
                />
              }
              label={machine ? `${stateName(machine)} starts here` : 'Starts here'}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {!machine
                ? 'Only a state inside a machine can be where it starts.'
                : initial && startsNow
                  ? `Moves the start from ${stateName(startsNow)}.`
                  : `Writes entry; then ${label || '…'}; in ${stateName(machine)}.`}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2">Transitions</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Each is declared in the machine both states belong to. To leaves the new state; From
              enters it.
            </Typography>
            {rows.length > 0 && (
              <Table size="small" aria-label="Transitions">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '6rem' }}>Direction</TableCell>
                    <TableCell>State</TableCell>
                    <TableCell>Trigger</TableCell>
                    <TableCell>Guard</TableCell>
                    <TableCell>Effect</TableCell>
                    <TableCell>Decided by</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow key={row.key}>
                      <TableCell>
                        <TextField
                          select
                          size="small"
                          variant="standard"
                          value={row.direction}
                          onChange={(event) => change(row.key, { direction: event.target.value as Direction })}
                          slotProps={{ htmlInput: { 'aria-label': `Direction ${index + 1}` } }}
                        >
                          <MenuItem value="to">To</MenuItem>
                          <MenuItem value="from">From</MenuItem>
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <ElementPicker
                          rows={states.rows}
                          // Only states a transition with the new one could be
                          // declared for - judged from where it will be.
                          where={(other) => !qualified
                            || canJoin(refs, qualified, String(other.qualified_name ?? ''))}
                          describe={(other, value) => ({
                            value, label: stateName(other), detail: String(other.qualified_name ?? ''),
                          })}
                          show="label"
                          value={row.state || null}
                          onChange={(next) => change(row.key, { state: next })}
                          ariaLabel={`State ${index + 1}`}
                          variant="standard"
                          sx={{ minWidth: 150 }}
                        />
                      </TableCell>
                      {(['trigger', 'guard', 'effect'] as const).map((field) => (
                        <TableCell key={field}>
                          <TextField
                            size="small"
                            variant="standard"
                            value={row[field] ?? ''}
                            onChange={(event) => change(row.key, { [field]: event.target.value })}
                            slotProps={{ htmlInput: { 'aria-label': `${field} ${index + 1}` } }}
                            sx={{ minWidth: 80 }}
                          />
                        </TableCell>
                      ))}
                      <TableCell>
                        <ElementPicker
                          rows={parts.rows}
                          where={offeredAsDecider}
                          strict={parts.complete ? 'part' : false}
                          value={row.decided_by || null}
                          onChange={(next) => change(row.key, { decided_by: next })}
                          ariaLabel={`Decided by ${index + 1}`}
                          variant="standard"
                          sx={{ minWidth: 140 }}
                        />
                      </TableCell>
                      <TableCell padding="none">
                        <IconButton
                          size="small"
                          aria-label={`Remove transition ${index + 1}`}
                          onClick={() => setRows((current) => current.filter((other) => other.key !== row.key))}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <Button
              size="small"
              startIcon={<AddIcon />}
              // Keyed on the clock, not the length: removing a row and adding
              // one would otherwise reuse a key React has just seen.
              onClick={() => setRows((current) => [...current, emptyRow(`row-${Date.now()}-${current.length}`)])}
            >
              Add transition
            </Button>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={busy || blocked}>
          {busy ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
